import { Spinner } from '@creditornot/cb-ingredients';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { Switch, Redirect, useRouteMatch, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { fetchEnterprise } from 'apis/enterprise/api';
import { Enterprise } from 'apis/enterprise/types';
import { RouteWhenAuthenticated } from 'common/components';
import useCountryConfig from 'common/hooks/useCountryConfig';
import PageLoadError from 'core/PageLoadError';
import EnterpriseUserManagement from 'modules/access-control/EnterpriseUserManagement';
import { CountryConfigContextProvider } from 'modules/country/CountryConfigContext';
import { CountryConfig } from 'modules/country/types';
import EnterpriseReporting from 'modules/enterprise/components/enterprise-reporting';
import EnterprisePurchaseData from 'modules/enterprise/components/purchase-history';
import { EnterpriseContextProvider } from 'modules/enterprise/context/EnterpriseContext';
import { getEnterpriseRoute } from 'modules/enterprise/utils/getEnterpriseRoute';
import HistoricalVenueAvailbility from 'modules/historical-venue-availability/HistoricalVenueAvailability';
import FeatureEnterpriseInsights from 'modules/insights-v2/feature/FeatureEnterpriseInsights';
import useInsightV2FeatureFlag from 'modules/insights-v2/hooks/useInsightV2FeatureFlag';
import { SidebarNavigationEnterpriseTemplate } from 'modules/sidebar/SidebarNavigationTemplate';
import VenueAvailabilityStatus from 'modules/venue-availability-status';

import { useTelemetryEnterpriseMetadata } from '../../modules/enterprise/hooks/telemetry';

type Params = {
  slug: string;
};

type Props = {
  countryConfig: CountryConfig;
  enterprise: Enterprise;
};

const EnterpriseRoutes = ({ countryConfig, enterprise }: Props) => {
  useTelemetryEnterpriseMetadata();
  const { path } = useRouteMatch<{ slug: string }>();
  const purchaseDataRoute = getEnterpriseRoute('purchase-data', enterprise.id);
  const enterpriseCountry = enterprise.country || '';

  const { isInsightV2Enabled } = useInsightV2FeatureFlag('enterprise', enterprise.id);

  const VenueAvailabilityStatusRoot = useMemo(
    () => () => <VenueAvailabilityStatus entity={{ type: 'enterprise', enterprise }} />,
    [enterprise],
  );

  const HistoricalVenueAvailabilityRoot = useMemo(
    () => () => <HistoricalVenueAvailbility entity={{ type: 'enterprise', enterprise }} />,
    [enterprise],
  );
  return (
    <SidebarNavigationEnterpriseTemplate
      enterpriseId={enterprise.id}
      enterpriseName={enterprise.name}
      enterpriseCountry={enterpriseCountry}
    >
      <CountryConfigContextProvider value={countryConfig}>
        <Switch>
          {/* Todo: Will remove after we have the enterprise index page */}
          <RouteWhenAuthenticated exact path={path}>
            <Redirect to={purchaseDataRoute} />
          </RouteWhenAuthenticated>
          <RouteWhenAuthenticated path={`${path}/purchase-data`} component={EnterprisePurchaseData} />
          <RouteWhenAuthenticated path={`${path}/reporting`} component={EnterpriseReporting} />
          {isInsightV2Enabled && (
            <RouteWhenAuthenticated path={`${path}/insights`} component={FeatureEnterpriseInsights} />
          )}
          <RouteWhenAuthenticated
            exact
            path={`${path}/venues/historical-venue-availability`}
            component={HistoricalVenueAvailabilityRoot}
          />

          <RouteWhenAuthenticated path={`${path}/venues`} component={VenueAvailabilityStatusRoot} />

          <RouteWhenAuthenticated path={`${path}/access`} component={EnterpriseUserManagement} />
        </Switch>
      </CountryConfigContextProvider>
    </SidebarNavigationEnterpriseTemplate>
  );
};

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

export default () => {
  const { slug: enterpriseId } = useParams<Params>();

  const {
    data: enterprise,
    isLoading: isLoadingEnterprise,
    isFetching,
  } = useQuery(
    [`/v1/enterprises/${enterpriseId}`],
    () => {
      return enterpriseId ? fetchEnterprise(enterpriseId) : undefined;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const { countryConfig, error: countryConfigError } = useCountryConfig(enterprise?.country ?? null);
  const isLoadingCountryConfig = !countryConfig && !countryConfigError;
  const isLoading = (isLoadingEnterprise && isFetching) || isLoadingCountryConfig;

  if (isLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" />
      </SpinnerContainer>
    );
  }

  if (enterprise && countryConfig) {
    return (
      <EnterpriseContextProvider value={{ enterprise }}>
        <EnterpriseRoutes countryConfig={countryConfig} enterprise={enterprise} />
      </EnterpriseContextProvider>
    );
  }

  return <PageLoadError />;
};
